import React, { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Card, Form, Input, Select, Space, Typography, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload';
import * as whatsappBroadcastService from '../../services/whatsappBroadcastService';
import { uploadImage } from '../../services/whatsappService';
import { WATemplate } from '../../types/whatsapp-broadcast';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const parsePhones = (raw: string): string[] => {
  const parts = raw
    .split(/[\n,\s]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  return Array.from(new Set(parts));
};

const BroadcastManual: React.FC = () => {
  const { message } = App.useApp();

  const [templates, setTemplates] = useState<WATemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [phonesRaw, setPhonesRaw] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<WATemplate | null>(null);
  const [bodyParams, setBodyParams] = useState<string[]>([]);
  const [headerImageUrl, setHeaderImageUrl] = useState<string | undefined>();
  const [uploadingHeader, setUploadingHeader] = useState(false);

  const [previewResult, setPreviewResult] = useState<{ count: number; skipped: number } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const r = await whatsappBroadcastService.listTemplates();
      setTemplates((r.data || []).filter((t: WATemplate) => t.enabled && t.status === 'APPROVED'));
    } catch (error) {
      console.error(error);
      message.error('Gagal memuat daftar template');
    } finally {
      setLoadingTemplates(false);
    }
  }, [message]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const phones = parsePhones(phonesRaw);

  const handleTemplateChange = (name: string) => {
    const t = templates.find((tpl) => tpl.name === name) || null;
    setSelectedTemplate(t);
    setBodyParams(t ? Array(t.body_param_count).fill('') : []);
    setHeaderImageUrl(undefined);
    setPreviewResult(null);
  };

  const handleHeaderUpload = async (file: RcFile) => {
    setUploadingHeader(true);
    try {
      const url = await uploadImage(file);
      setHeaderImageUrl(url);
    } catch (error) {
      console.error(error);
      message.error('Gagal mengunggah gambar header');
    } finally {
      setUploadingHeader(false);
    }
    return false;
  };

  const handlePreview = async () => {
    if (phones.length === 0) {
      message.warning('Masukkan minimal satu nomor tujuan');
      return;
    }
    setPreviewing(true);
    try {
      const r = await whatsappBroadcastService.previewBroadcast({ phones });
      setPreviewResult({ count: r.data?.count ?? 0, skipped: r.data?.skipped ?? 0 });
    } catch (error: any) {
      console.error(error);
      message.error(error?.response?.data?.message || 'Gagal memuat preview audiens');
    } finally {
      setPreviewing(false);
    }
  };

  const canSend =
    !!selectedTemplate &&
    phones.length > 0 &&
    bodyParams.every((p) => p.trim().length > 0) &&
    (selectedTemplate.header_type !== 'image' || !!headerImageUrl);

  const handleSubmit = async () => {
    if (!selectedTemplate) return;
    setSubmitting(true);
    try {
      const r = await whatsappBroadcastService.createBroadcast({
        template_name: selectedTemplate.name,
        template_language: selectedTemplate.language,
        body_params: bodyParams,
        header_image_url: headerImageUrl,
        phones,
      });
      message.success(`Broadcast berhasil dibuat untuk ${r.data?.total ?? phones.length} nomor`);
      setPhonesRaw('');
      setSelectedTemplate(null);
      setBodyParams([]);
      setHeaderImageUrl(undefined);
      setPreviewResult(null);
    } catch (error: any) {
      console.error(error);
      message.error(error?.response?.data?.message || 'Gagal membuat broadcast');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Title level={2}>Broadcast Manual WhatsApp</Title>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Nomor otomatis dinormalisasi ke format 62. Pengiriman nyata aktif setelah dispatcher dinyalakan."
      />

      <Card bordered={false}>
        <Form layout="vertical">
          <Form.Item label="Nomor Tujuan" required>
            <TextArea
              rows={6}
              placeholder={'Tempel nomor, satu per baris atau dipisah koma/spasi\n6281234567890\n6281298765432'}
              value={phonesRaw}
              onChange={(e) => setPhonesRaw(e.target.value)}
            />
            <Text type="secondary">{phones.length} nomor terdeteksi</Text>
          </Form.Item>

          <Form.Item label="Template (Aktif & APPROVED)" required>
            <Select
              placeholder="Pilih template"
              loading={loadingTemplates}
              value={selectedTemplate?.name}
              onChange={handleTemplateChange}
              showSearch
              optionFilterProp="children"
            >
              {templates.map((t) => (
                <Option key={t.id} value={t.name}>
                  {t.name} ({t.language})
                </Option>
              ))}
            </Select>
            {templates.length === 0 && !loadingTemplates && (
              <Text type="secondary">
                Belum ada template Aktif & APPROVED. Aktifkan dulu di halaman Template WhatsApp.
              </Text>
            )}
          </Form.Item>

          {selectedTemplate && bodyParams.length > 0 && (
            <>
              <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                Isi nilai literal untuk tiap parameter body (bukan placeholder lead, karena broadcast manual tidak
                terkait data lead).
              </Text>
              {bodyParams.map((val, idx) => (
                <Form.Item key={idx} label={`Parameter {{${idx + 1}}}`}>
                  <Input
                    value={val}
                    placeholder="nilai literal"
                    onChange={(e) => {
                      const next = [...bodyParams];
                      next[idx] = e.target.value;
                      setBodyParams(next);
                    }}
                  />
                </Form.Item>
              ))}
            </>
          )}

          {selectedTemplate?.header_type === 'image' && (
            <Form.Item label="Gambar Header" required>
              <Upload accept="image/*" showUploadList={false} beforeUpload={handleHeaderUpload}>
                <Button icon={<UploadOutlined />} loading={uploadingHeader}>
                  {headerImageUrl ? 'Ganti gambar' : 'Unggah gambar'}
                </Button>
              </Upload>
              {headerImageUrl && (
                <div style={{ marginTop: 8 }}>
                  <img src={headerImageUrl} alt="header" style={{ maxWidth: 160, borderRadius: 6 }} />
                </div>
              )}
            </Form.Item>
          )}

          <Space style={{ marginTop: 8 }}>
            <Button onClick={handlePreview} loading={previewing}>
              Preview Audiens
            </Button>
            {previewResult && (
              <Text>
                Audiens: <strong>{previewResult.count}</strong> nomor valid, {previewResult.skipped} dilewati
                (invalid/duplikat/opt-out).
              </Text>
            )}
          </Space>

          <div style={{ marginTop: 24 }}>
            <Button type="primary" disabled={!canSend} loading={submitting} onClick={handleSubmit}>
              Kirim Broadcast
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default BroadcastManual;
